/** /lti/deep-link?dl=… (STORY-092): a teacher who launched a deep-linking request from the
 * platform picks the course the activity should open; the service returns a signed response
 * as an auto-submitting form, which this page hands to the browser. */
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { serviceUrl } from "../api/base";
import { accessToken } from "../auth/session";
import { listCourses } from "../teacher/api";
import { Button } from "../ui";

export function LtiDeepLink() {
  const { t } = useTranslation();
  const dl = new URLSearchParams(location.search).get("dl") ?? "";
  const courses = useQuery({ queryKey: ["courses"], queryFn: listCourses });
  const [picked, setPicked] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const send = useMutation({
    mutationFn: async () => {
      const course = courses.data?.find((c) => c.course_id === picked);
      const r = await fetch(serviceUrl(`/integrations/v1/lti/deep-link/return`), {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken() ?? ""}` },
        body: JSON.stringify({ dl, course_id: picked, title: course?.title ?? "" }),
      });
      if (!r.ok) throw new Error(`${r.status}`);
      return r.text();
    },
    onSuccess: (html) => { document.open(); document.write(html); document.close(); },
    onError: (e) => setErr(String(e)),
  });
  return (
    <main className="mx-auto flex max-w-lg flex-col gap-4 p-6">
      <h1 className="display text-2xl">{t("lti.pickTitle")}</h1>
      <p className="text-sm text-[color:var(--ink-2)]">{t("lti.pickBody")}</p>
      {!dl && <p role="alert" className="text-sm">{t("lti.noSession")}</p>}
      <ul className="flex flex-col gap-2" role="radiogroup" aria-label={t("lti.pickTitle")}>
        {(courses.data ?? []).map((c) => (
          <li key={c.course_id}>
            <label className="flex min-h-[var(--control-h)] cursor-pointer items-center gap-3 rounded-[var(--radius-card)] bg-[color:var(--surface)] p-3 shadow-sm">
              <input type="radio" name="course" value={c.course_id} checked={picked === c.course_id} onChange={() => setPicked(c.course_id)} />
              <span>{c.title}</span>
            </label>
          </li>
        ))}
        {courses.data && courses.data.length === 0 && <li className="text-sm text-[color:var(--ink-2)]">{t("lti.noCourses")}</li>}
      </ul>
      <Button disabled={!picked || !dl} loading={send.isPending} onClick={() => send.mutate()}>{t("lti.link")}</Button>
      {err && <p role="alert" className="text-sm" style={{ color: "var(--danger)" }}>{t("lti.failed")} ({err})</p>}
    </main>
  );
}
